import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import useEmblaCarousel from 'embla-carousel-react';
import { MapPin, ShieldCheck, Gem, Package, Droplets, Clock, UtensilsCrossed, Coffee, Leaf, Gift, FileCheck, Hand, Lock, Flame, ArrowRight, ZoomIn, X, ChevronLeft, ChevronRight } from 'lucide-react';

const productImages = [
  {
    src: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_828e32ce-193c-4c51-b926-dcf4aea89e6f/artifacts/ocion9gb_WhatsApp%20Image%202026-07-24%20at%2010.45.41%20AM.jpeg',
    alt: 'Hind Harvest Kesar - Grade-1 Kashmiri Saffron',
  },
  {
    src: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_828e32ce-193c-4c51-b926-dcf4aea89e6f/artifacts/c9vcdlze_WhatsApp%20Image%202026-07-23%20at%2011.17.59%20PM.jpeg',
    alt: 'From the Fields of Pampore, Kashmir',
  },
  {
    src: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_828e32ce-193c-4c51-b926-dcf4aea89e6f/artifacts/tjnrd4q2_WhatsApp%20Image%202026-07-23%20at%2011.39.33%20PM.jpeg',
    alt: 'Premium Packaging That Preserves Purity',
  },
  {
    src: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_828e32ce-193c-4c51-b926-dcf4aea89e6f/artifacts/gk33ndpp_WhatsApp%20Image%202026-07-24%20at%2010.30.18%20AM.jpeg',
    alt: 'Kesar Made Easy - Usage Instructions',
  },
  {
    src: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_828e32ce-193c-4c51-b926-dcf4aea89e6f/artifacts/jekubwpu_WhatsApp%20Image%202026-07-23%20at%2011.31.14%20PM.jpeg',
    alt: 'A Touch of Kesar, A World of Goodness',
  },
  {
    src: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_828e32ce-193c-4c51-b926-dcf4aea89e6f/artifacts/y9zgyuve_WhatsApp%20Image%202026-07-23%20at%207.57.31%20PM.jpeg',
    alt: 'The Power of Pure Saffron - Health Benefits',
  },
];

const WHATSAPP_URL = `https://wa.me/919930290100?text=${encodeURIComponent('Hi, I would like to order Hind Harvest Kashmiri Kesar – 1g.')}`;

const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const GoldDivider = () => (
  <div className="flex items-center justify-center gap-3 my-1">
    <span className="block w-10 h-px bg-gold" />
    <span className="text-gold text-xs tracking-widest select-none" aria-hidden="true">&#10022;</span>
    <span className="block w-10 h-px bg-gold" />
  </div>
);

const SectionHeading = ({ title, subtitle }) => (
  <div className="text-center mb-12 lg:mb-16">
    <h2 className="text-3xl lg:text-4xl font-bold text-maroon mb-3">{title}</h2>
    <GoldDivider />
    {subtitle && (
      <p className="text-gray-600 max-w-2xl mx-auto mt-4 leading-relaxed">{subtitle}</p>
    )}
  </div>
);

const KesarProduct = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });

  const onEmblaSelect = useCallback(() => {
    if (!emblaApi) return;
    setActiveIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on('select', onEmblaSelect);
    onEmblaSelect();
    return () => emblaApi.off('select', onEmblaSelect);
  }, [emblaApi, onEmblaSelect]);

  const scrollTo = useCallback(
    (index) => {
      if (emblaApi) emblaApi.scrollTo(index);
      setActiveIndex(index);
    },
    [emblaApi]
  );

  // === Lightbox state ===
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [zoomed, setZoomed] = useState(false);
  const [panPos, setPanPos] = useState({ x: 0, y: 0 });
  const touchRef = useRef({ startX: 0, startY: 0, dist: 0 });
  const imgContainerRef = useRef(null);

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setZoomed(false);
    setPanPos({ x: 0, y: 0 });
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setActiveIndex(lightboxIndex);
    if (emblaApi) emblaApi.scrollTo(lightboxIndex);
    setLightboxOpen(false);
    setZoomed(false);
    setPanPos({ x: 0, y: 0 });
    document.body.style.overflow = '';
  };

  const lightboxPrev = () => {
    setZoomed(false);
    setPanPos({ x: 0, y: 0 });
    setLightboxIndex((prev) => (prev - 1 + productImages.length) % productImages.length);
  };

  const lightboxNext = () => {
    setZoomed(false);
    setPanPos({ x: 0, y: 0 });
    setLightboxIndex((prev) => (prev + 1) % productImages.length);
  };

  const toggleZoom = (e) => {
    if (zoomed) {
      setZoomed(false);
      setPanPos({ x: 0, y: 0 });
    } else {
      setZoomed(true);
      if (imgContainerRef.current) {
        const rect = imgContainerRef.current.getBoundingClientRect();
        const xPct = ((e.clientX - rect.left) / rect.width - 0.5) * -100;
        const yPct = ((e.clientY - rect.top) / rect.height - 0.5) * -100;
        setPanPos({ x: xPct, y: yPct });
      }
    }
  };

  const handleMouseMove = (e) => {
    if (!zoomed || !imgContainerRef.current) return;
    const rect = imgContainerRef.current.getBoundingClientRect();
    const xPct = ((e.clientX - rect.left) / rect.width - 0.5) * -100;
    const yPct = ((e.clientY - rect.top) / rect.height - 0.5) * -100;
    setPanPos({ x: xPct, y: yPct });
  };

  // Keyboard navigation
  useEffect(() => {
    if (!lightboxOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowLeft') lightboxPrev();
      else if (e.key === 'ArrowRight') lightboxNext();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  });

  // Touch swipe for lightbox
  const onTouchStart = (e) => {
    if (zoomed) return;
    touchRef.current.startX = e.touches[0].clientX;
    touchRef.current.startY = e.touches[0].clientY;
    touchRef.current.dist = 0;
  };

  const onTouchMove = (e) => {
    if (zoomed) return;
    touchRef.current.dist = e.touches[0].clientX - touchRef.current.startX;
  };

  const onTouchEnd = () => {
    if (zoomed) return;
    const d = touchRef.current.dist;
    if (d > 60) lightboxPrev();
    else if (d < -60) lightboxNext();
  };

  return (
    <div className="pt-16 sm:pt-20 bg-cream-50">

      {/* ===== LIGHTBOX MODAL ===== */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          data-testid="lightbox-overlay"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/90"
            onClick={closeLightbox}
          />

          {/* Close button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors duration-200"
            data-testid="lightbox-close-btn"
            aria-label="Close lightbox"
          >
            <X size={22} />
          </button>

          {/* Counter */}
          <div className="absolute top-5 left-5 z-10 text-white/60 text-sm font-medium">
            {lightboxIndex + 1} / {productImages.length}
          </div>

          {/* Prev arrow */}
          <button
            onClick={lightboxPrev}
            className="absolute left-3 sm:left-6 z-10 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors duration-200"
            data-testid="lightbox-prev-btn"
            aria-label="Previous image"
          >
            <ChevronLeft size={24} />
          </button>

          {/* Next arrow */}
          <button
            onClick={lightboxNext}
            className="absolute right-3 sm:right-6 z-10 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors duration-200"
            data-testid="lightbox-next-btn"
            aria-label="Next image"
          >
            <ChevronRight size={24} />
          </button>

          {/* Image container */}
          <div
            ref={imgContainerRef}
            className="relative z-[5] max-w-[90vw] max-h-[85vh] flex items-center justify-center overflow-hidden select-none"
            onClick={toggleZoom}
            onMouseMove={handleMouseMove}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            style={{ cursor: zoomed ? 'zoom-out' : 'zoom-in' }}
          >
            <img
              src={productImages[lightboxIndex].src}
              alt={productImages[lightboxIndex].alt}
              className="max-w-full max-h-[85vh] object-contain transition-transform duration-200 ease-out"
              style={{
                transform: zoomed
                  ? `scale(2.2) translate(${panPos.x * 0.4}%, ${panPos.y * 0.4}%)`
                  : 'scale(1)',
              }}
              draggable={false}
            />
          </div>

          {/* Zoom hint */}
          {!zoomed && (
            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-10 text-white/50 text-xs flex items-center gap-1.5">
              <ZoomIn size={14} />
              <span className="hidden sm:inline">Click to zoom</span>
              <span className="sm:hidden">Tap to zoom</span>
            </div>
          )}
        </div>
      )}

      {/* ===== GALLERY + PRODUCT INFO ===== */}
      <section className="py-8 lg:py-16" data-testid="product-gallery-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">

            {/* Gallery Column */}
            <div>
              {/* Mobile Carousel */}
              <div className="lg:hidden">
                <div className="overflow-hidden rounded-xl" ref={emblaRef}>
                  <div className="flex">
                    {productImages.map((img, i) => (
                      <div key={i} className="flex-[0_0_100%] min-w-0">
                        <div
                          className="bg-cream-100 rounded-xl flex items-center justify-center p-3 relative cursor-pointer"
                          onClick={() => openLightbox(i)}
                        >
                          <img
                            src={img.src}
                            alt={img.alt}
                            className="w-full object-contain max-h-[480px] rounded-lg"
                            loading={i === 0 ? 'eager' : 'lazy'}
                          />
                          <div className="absolute bottom-5 right-5 w-8 h-8 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center text-white pointer-events-none">
                            <ZoomIn size={16} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex justify-center gap-2 mt-4" data-testid="carousel-dots">
                  {productImages.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => scrollTo(i)}
                      className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                        i === activeIndex ? 'bg-maroon scale-110' : 'bg-gray-300'
                      }`}
                      aria-label={`Go to image ${i + 1}`}
                      data-testid={`carousel-dot-${i}`}
                    />
                  ))}
                </div>
              </div>

              {/* Desktop Gallery */}
              <div className="hidden lg:block">
                <div
                  className="bg-cream-100 rounded-xl overflow-hidden flex items-center justify-center p-4 min-h-[500px] relative cursor-pointer group"
                  data-testid="main-product-image"
                  onClick={() => openLightbox(activeIndex)}
                >
                  <img
                    src={productImages[activeIndex].src}
                    alt={productImages[activeIndex].alt}
                    className="max-w-full max-h-[560px] object-contain rounded-lg transition-opacity duration-300"
                  />
                  <div className="absolute bottom-6 right-6 w-10 h-10 rounded-full bg-black/25 backdrop-blur-sm flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                    <ZoomIn size={20} />
                  </div>
                </div>
                <div className="grid grid-cols-6 gap-3 mt-4" data-testid="thumbnail-strip">
                  {productImages.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveIndex(i)}
                      className={`rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                        i === activeIndex
                          ? 'border-maroon ring-1 ring-maroon/30'
                          : 'border-transparent opacity-60 hover:opacity-100'
                      }`}
                      data-testid={`thumbnail-${i}`}
                    >
                      <img
                        src={img.src}
                        alt={img.alt}
                        className="w-full aspect-square object-cover"
                        loading="lazy"
                      />
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Product Info Column */}
            <div className="lg:sticky lg:top-28 space-y-6" data-testid="product-info">
              <div>
                <p className="text-sm font-medium tracking-widest text-gold uppercase mb-2">
                  Hind Harvest
                </p>
                <h1
                  className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold text-maroon leading-tight"
                  data-testid="product-title"
                >
                  Kashmiri Kesar
                </h1>
                <p className="text-base text-gray-500 mt-2">Grade-1 Kashmiri Saffron</p>
              </div>

              <div className="flex items-center gap-2 text-sm text-gray-500">
                <MapPin size={15} className="text-gold flex-shrink-0" />
                <span>Sourced from Pampore, Kashmir</span>
              </div>

              <p className="text-sm text-gray-500">
                Net Weight: <span className="font-semibold text-gray-700">1 g</span>
              </p>

              {/* Price */}
              <div className="flex items-baseline gap-3" data-testid="product-price">
                <span className="text-3xl font-bold text-maroon">&#8377;499</span>
                <span className="text-lg text-gray-400 line-through">&#8377;599</span>
              </div>

              {/* WhatsApp CTA */}
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 w-full px-8 py-4 bg-maroon text-white font-semibold rounded-xl hover:opacity-90 transition-all duration-300 hover:shadow-lg text-lg"
                data-testid="whatsapp-order-btn"
              >
                <WhatsAppIcon className="w-6 h-6" />
                Order on WhatsApp
              </a>

              {/* Trust Markers */}
              <div
                className="flex flex-wrap items-center gap-x-5 gap-y-3 pt-4 border-t border-cream-200"
                data-testid="trust-markers"
              >
                {[
                  { Icon: MapPin, label: 'Pampore Origin' },
                  { Icon: Hand, label: 'Handpicked' },
                  { Icon: ShieldCheck, label: 'Lab Tested' },
                  { Icon: Leaf, label: 'No Additives' },
                ].map(({ Icon, label }, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-sm text-gray-500">
                    <Icon size={14} className="text-gold" />
                    <span>{label}</span>
                  </div>
                ))}
              </div>

              {/* Learn More Link */}
              <Link
                to="/products/kesar/guide"
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-maroon bg-cream-50 border border-maroon/30 rounded-lg hover:bg-cream-100 hover:border-maroon/50 transition-all duration-200"
                data-testid="learn-more-kesar-btn"
              >
                Learn More About Kesar
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FROM PAMPORE TO YOUR HOME ===== */}
      <section className="py-16 lg:py-24 bg-white" data-testid="pampore-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <SectionHeading title="From Pampore to Your Home" />
          <p className="text-gray-600 leading-relaxed text-base lg:text-lg">
            In the high-altitude fields of Pampore, Kashmir, crocus flowers bloom each autumn in a
            fleeting display of purple and gold. At dawn, skilled hands carefully pluck each delicate
            flower and gently separate the precious red stigmas &mdash; the world&#8217;s most
            treasured spice. This centuries-old tradition of patience and precision is what gives
            Hind Harvest Kesar its distinctive character. From harvest to your home, every strand is
            treated with care to preserve the rich aroma, deep colour and potent goodness that only
            authentic Kashmiri saffron can offer.
          </p>
        </div>
      </section>

      {/* ===== WHY HIND HARVEST KESAR ===== */}
      <section className="py-16 lg:py-24 bg-cream-50" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading title="Why Hind Harvest Kesar" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                Icon: MapPin,
                title: 'Pampore Origin',
                desc: 'Sourced from the famed saffron fields of Pampore, Kashmir — where the finest kesar has been cultivated for centuries.',
              },
              {
                Icon: ShieldCheck,
                title: '100% Pure',
                desc: 'Every batch is tested to ensure complete purity. No artificial colours, no additives — just pure saffron.',
              },
              {
                Icon: Gem,
                title: 'Carefully Selected',
                desc: 'Each strand is handpicked at dawn and gently separated to preserve aroma, colour and potency.',
              },
              {
                Icon: Package,
                title: 'Premium Packaging',
                desc: 'Packed in an airtight glass jar and premium outer box that protect freshness, aroma and quality.',
              },
            ].map(({ Icon, title, desc }, i) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 mx-auto rounded-full bg-gold/10 flex items-center justify-center mb-5">
                  <Icon size={24} className="text-gold" />
                </div>
                <h3 className="text-lg font-semibold text-maroon mb-2">{title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== HOW TO USE KESAR ===== */}
      <section className="py-16 lg:py-24 bg-white" data-testid="how-to-use-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            title="How to Use Kesar"
            subtitle="A simple ritual for everyday elegance. No complicated steps &mdash; just pure saffron."
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            {[
              {
                step: '1',
                title: 'Take a Few Strands',
                desc: 'Use only a small quantity. A few strands go a long way.',
              },
              {
                step: '2',
                title: 'Soak in Warm Liquid',
                desc: 'Place strands in warm milk or water and let them soak for 15–20 minutes. Use warm, not boiling.',
              },
              {
                step: '3',
                title: 'Add to Your Dish',
                desc: 'Add the infused liquid and strands to milk, tea, desserts or food for rich aroma and colour.',
              },
            ].map(({ step, title, desc }, i) => (
              <div key={i} className="text-center">
                <div className="w-12 h-12 mx-auto rounded-full bg-maroon text-white flex items-center justify-center font-bold text-lg mb-5">
                  {step}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== WAYS TO ENJOY IT ===== */}
      <section className="py-16 lg:py-24 bg-cream-50" data-testid="ways-to-enjoy-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            title="Ways to Enjoy It"
            subtitle="Elevate everyday moments with the richness of Kashmiri saffron."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                Icon: Coffee,
                title: 'Kesar Milk',
                desc: 'A warm, golden cup of saffron-infused milk that soothes and nourishes.',
              },
              {
                Icon: UtensilsCrossed,
                title: 'Kheer & Desserts',
                desc: 'Adds aroma, colour and a luxurious finishing touch to your favourite sweets.',
              },
              {
                Icon: Flame,
                title: 'Biryani & Curries',
                desc: 'For rich aroma, beautiful golden hue and unforgettable flavour.',
              },
              {
                Icon: Leaf,
                title: 'Kesar Tea',
                desc: 'A fragrant saffron infusion for calm, peaceful moments.',
              },
            ].map(({ Icon, title, desc }, i) => (
              <div
                key={i}
                className="bg-white rounded-xl p-6 text-center shadow-sm hover:shadow-md transition-shadow duration-300"
              >
                <div className="w-12 h-12 mx-auto rounded-full bg-gold/10 flex items-center justify-center mb-4">
                  <Icon size={22} className="text-gold" />
                </div>
                <h3 className="text-lg font-semibold text-maroon mb-2">{title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== WHAT YOU RECEIVE ===== */}
      <section className="py-16 lg:py-24 bg-white" data-testid="what-you-receive-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading title="What You Receive" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            {[
              {
                Icon: Package,
                title: '1g Glass Jar',
                desc: 'Premium airtight glass jar filled with Grade-1 Kashmiri saffron threads.',
              },
              {
                Icon: Gift,
                title: 'Premium Outer Box',
                desc: 'Beautifully designed packaging that protects and makes a perfect gift.',
              },
              {
                Icon: FileCheck,
                title: 'Lab-Test Certificate',
                desc: 'Certificate of purity and authenticity included with every jar.',
              },
            ].map(({ Icon, title, desc }, i) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 mx-auto rounded-full bg-gold/10 flex items-center justify-center mb-5">
                  <Icon size={24} className="text-gold" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== STORAGE ===== */}
      <section className="py-12 lg:py-16 bg-cream-50" data-testid="storage-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center">
              <Lock size={18} className="text-gold" />
            </div>
          </div>
          <h3 className="text-xl font-semibold text-maroon mb-3">Storage</h3>
          <p className="text-gray-600 leading-relaxed">
            Store tightly closed in a cool, dry place away from direct light. A few strands go a
            long way &mdash; this 1g jar will last through many uses.
          </p>
        </div>
      </section>

      {/* ===== BOTTOM CTA ===== */}
      <section className="py-16 lg:py-24 bg-maroon" data-testid="bottom-cta-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <GoldDivider />
          <h2 className="text-3xl lg:text-4xl font-bold text-white mt-6 mb-4">
            Bring Home Authentic Kashmiri Kesar
          </h2>
          <p className="text-cream-200 mb-8 text-base lg:text-lg">
            Pure, natural and hand-selected &mdash; from the fields of Pampore to your home.
          </p>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-3 px-10 py-4 bg-white text-maroon font-semibold rounded-xl hover:bg-cream-100 transition-all duration-300 hover:shadow-lg text-lg"
            data-testid="bottom-whatsapp-btn"
          >
            <WhatsAppIcon className="w-6 h-6" />
            Order on WhatsApp
          </a>
        </div>
      </section>
    </div>
  );
};

export default KesarProduct;
